import {useState} from "react";
import {common as stringsCommon, changePassword as strings} from "strings";

import Button from "components/Button";
import Divider from "components/Divider";
import MobileNavigationHeader from "components/MobileNavigationHeader";
import PageTitle from "components/PageTitle";
import PasswordRequirements from "components/PasswordRequirements/PasswordRequirements";
import TextInput from "components/TextInput";

import {useChangePassword} from "liberty-core/api/react-query/user";

import {useAlert} from "lib/alert";

import styles from "./ChangePasswordPage.module.css";

type Errors = {
  PreviousPassword?: string[];
  NewPassword?: string[];
  ConfirmNewPassword?: string[];
};

function ChangePasswordForm() {
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmNewPassword, setConfirmNewPassword] = useState("");

  const [newPasswordErrorText, setNewPasswordErrorText] = useState("");
  const [confirmPasswordErrorText, setConfirmPasswordErrorText] = useState("");
  const [errors, setErrors] = useState<Errors>({});

  const alert = useAlert();

  const {mutateAsync: changePassword} = useChangePassword();

  return (
    <div className={styles.changePasswordForm}>
      <div className={styles.inputFieldContainer}>
        <TextInput
          className={styles.inputField}
          label={strings.currentPassword}
          type="password"
          autoComplete="current-password"
          value={currentPassword}
          onChangeText={setCurrentPassword}
          errorText={errors.PreviousPassword?.join("\n")}
        />
        <TextInput
          className={styles.inputField}
          label={strings.newPassword}
          type={newPasswordErrorText === "" ? "password" : "text"}
          autoComplete="new-password"
          value={newPassword}
          onChangeText={setNewPassword}
          errorText={errors.NewPassword?.join("\n")}
          onFocus={() => setNewPasswordErrorText("")}
        />
        <TextInput
          className={styles.inputField}
          label={strings.confirmNewPassword}
          type={confirmPasswordErrorText === "" ? "password" : "text"}
          autoComplete="new-password"
          value={confirmNewPassword}
          onChangeText={setConfirmNewPassword}
          errorText={errors.ConfirmNewPassword?.join("\n")}
          onFocus={() => setConfirmPasswordErrorText("")}
        />
        <PasswordRequirements password={newPassword} />
      </div>
      <div className={styles.saveButtonContainer}>
        <Button
          className={styles.saveButton}
          disabled={
            currentPassword.length === 0 ||
            newPassword.length === 0 ||
            confirmNewPassword.length === 0
          }
          onClick={() => {
            if (newPassword !== confirmNewPassword) {
              alert({
                title: stringsCommon.whoops,
                message: strings.passwordMatchError,
              });
              setErrors({
                NewPassword: [strings.fieldsMustMatch],
                ConfirmNewPassword: [strings.fieldsMustMatch],
              });
            } else {
              changePassword({
                previousPassword: currentPassword,
                newPassword: newPassword,
              })
                .then(() => {
                  alert({
                    title: stringsCommon.success,
                    message: strings.successBody,
                  });
                  setCurrentPassword("");
                  setNewPassword("");
                  setConfirmNewPassword("");
                })
                .catch((error) => {
                  if (
                    error?.response?.status === 400 &&
                    error?.response?.data?.errors
                  ) {
                    setErrors(error.response.data.errors);
                  }
                  alert({
                    title: stringsCommon.whoops,
                    message:
                      error.response?.data?.generic?.join("\n") ??
                      stringsCommon.somethingWentWrong,
                  });
                });
            }
          }}
        >
          {strings.save}
        </Button>
      </div>
    </div>
  );
}

export default function ChangePasswordPage() {
  return (
    <div className={styles.container}>
      <MobileNavigationHeader backTo="/profile" />
      <PageTitle>{strings.title}</PageTitle>
      <Divider className={styles.divider} />
      <ChangePasswordForm />
    </div>
  );
}
